import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, Filter, Eye, CheckCircle, Clock, XCircle, Loader, X, RefreshCw, Edit } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { metaApiService } from '../../services/metaApiService';
import { metaTemplateService } from '../../services/metaTemplateService';
import { WhatsAppTemplateDetailed, CreateTemplateRequest, TemplateComponent } from '../../types/FireAgent';
import { useNotification } from '../../hooks/useNotification';

const Templates: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showInfo, NotificationComponent } = useNotification();
  const [templates, setTemplates] = useState<WhatsAppTemplateDetailed[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'APPROVED' | 'PENDING' | 'REJECTED'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<'ALL' | 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'>('ALL');
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplateDetailed | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<WhatsAppTemplateDetailed | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      if (!userProfile?.empresaCnpj) {
        showError('Erro', 'CNPJ da empresa não encontrado');
        return;
      }

      const metaApiConfig = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
      if (!metaApiConfig || !metaApiConfig.accessToken || !metaApiConfig.businessAccountId) {
        showInfo('Configuração Necessária', 'Configure o Meta API em Configurações antes de gerenciar templates.');
        setTemplates([]);
        return;
      }

      metaApiService.setConfig(metaApiConfig);
      metaTemplateService.setConfig(metaApiConfig);

      const templatesList = await metaApiService.listAllTemplates();
      setTemplates(templatesList);
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      showError('Erro', error.message || 'Erro ao carregar templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

      // Agrupar templates por nome para identificar versões
      const templatesByName = new Map<string, WhatsAppTemplateDetailed[]>();
      templates.forEach(template => {
        const existing = templatesByName.get(template.name) || [];
        existing.push(template);
        templatesByName.set(template.name, existing);
      });

      // Adicionar informação de versão para templates com mesmo nome
      const templatesWithVersion = templates.map(template => {
        const sameNameTemplates = templatesByName.get(template.name) || [];
        const sortedByDate = sameNameTemplates.sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          return dateB - dateA; // Mais recente primeiro
        });
        const versionIndex = sortedByDate.findIndex(t => t.id === template.id);
        const isLatest = versionIndex === 0;
        const versionNumber = sameNameTemplates.length > 1 ? versionIndex + 1 : null;
        
        return {
          ...template,
          versionNumber,
          isLatest,
          totalVersions: sameNameTemplates.length
        };
      });

      const filteredTemplates = templatesWithVersion.filter(template => {
        const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'ALL' || template.status === statusFilter;
        const matchesCategory = categoryFilter === 'ALL' || template.category === categoryFilter;
        return matchesSearch && matchesStatus && matchesCategory;
      });

  const getStatusBadge = (status: string, showIcon: boolean = false) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center gap-1">
            {showIcon && <CheckCircle className="h-3 w-3" />}
            Aprovado
          </span>
        );
      case 'PENDING':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
            {showIcon && <Clock className="h-3 w-3" />}
            Pendente
          </span>
        );
      case 'REJECTED':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center gap-1">
            {showIcon && <XCircle className="h-3 w-3" />}
            Rejeitado
          </span>
        );
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'MARKETING':
        return 'Marketing';
      case 'UTILITY':
        return 'Utilidade';
      case 'AUTHENTICATION':
        return 'Autenticação';
      default:
        return category;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleViewDetails = async (template: WhatsAppTemplateDetailed) => {
    try {
      if (!template.id) return;
      
      const detailed = await metaTemplateService.getTemplateDetails(template.id);
      setSelectedTemplate(detailed);
    } catch (error: any) {
      showError('Erro', error.message || 'Erro ao buscar detalhes do template');
    }
  };

  const handleCreateTemplate = async (request: CreateTemplateRequest) => {
    try {
      setCreating(true);
      
      if (!userProfile?.empresaCnpj) {
        throw new Error('CNPJ da empresa não encontrado');
      }

      const metaApiConfig = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
      if (!metaApiConfig) {
        throw new Error('Configuração do Meta API não encontrada');
      }

      metaTemplateService.setConfig(metaApiConfig);

      await metaTemplateService.createTemplate(request);
      showSuccess('Sucesso', 'Template criado e enviado para análise');
      setShowCreateModal(false);
      await loadTemplates();
    } catch (error: any) {
      console.error('Erro ao criar template:', error);
      showError('Erro', error.message || 'Erro ao criar template');
    } finally {
      setCreating(false);
    }
  };

  const handleEditTemplate = (template: WhatsAppTemplateDetailed) => {
    setEditingTemplate(template);
  };

  const handleUpdateTemplate = async (request: CreateTemplateRequest) => {
    try {
      if (!editingTemplate?.id) return;
      
      setUpdating(true);
      
      if (!userProfile?.empresaCnpj) {
        throw new Error('CNPJ da empresa não encontrado');
      }

      const metaApiConfig = await firebaseService.getMetaApiConfig(userProfile.empresaCnpj);
      if (!metaApiConfig) {
        throw new Error('Configuração do Meta API não encontrada');
      }

      metaTemplateService.setConfig(metaApiConfig);

      await metaTemplateService.updateTemplate(editingTemplate.id, request);
      showSuccess('Sucesso', 'Nova versão do template criada e enviada para análise');
      setEditingTemplate(null);
      await loadTemplates();
    } catch (error: any) {
      console.error('Erro ao atualizar template:', error);
      showError('Erro', error.message || 'Erro ao atualizar template');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Templates WhatsApp</h1>
        <p className="text-gray-600">Gerencie seus templates de mensagens do WhatsApp</p>
      </div>

      <div className="card p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar template por nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input w-full pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="input"
            >
              <option value="ALL">Todos os status</option>
              <option value="APPROVED">Aprovado</option>
              <option value="PENDING">Pendente</option>
              <option value="REJECTED">Rejeitado</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as any)}
              className="input"
            >
              <option value="ALL">Todas as categorias</option>
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utilidade</option>
              <option value="AUTHENTICATION">Autenticação</option>
            </select>
            <button
              onClick={() => loadTemplates()}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                console.log('Botão Criar Template clicado');
                setShowCreateModal(true);
              }}
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Novo Template
            </button>
          </div>
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">
              {templates.length === 0
                ? 'Nenhum template encontrado. Crie seu primeiro template.'
                : 'Nenhum template corresponde aos filtros selecionados.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Nome</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Versão</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Categoria</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Idioma</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Criado em</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">ID</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-700">Ações</th>
                    </tr>
                  </thead>
              <tbody>
                    {filteredTemplates.map((template: any) => (
                      <tr key={template.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-medium text-gray-900">{template.name}</div>
                        </td>
                        <td className="py-3 px-4">
                          {template.totalVersions > 1 ? (
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                template.isLatest 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                v{template.versionNumber}
                                {template.isLatest && ' (mais recente)'}
                              </span>
                              {template.isLatest && (
                                <span className="text-xs text-green-600" title="Versão mais recente">
                                  ✓
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(template.status)}
                        {template.status === 'PENDING' && (
                          <span className="text-xs text-gray-500">Aguardando aprovação</span>
                        )}
                        {template.status === 'REJECTED' && template.rejection_reason && (
                          <span className="text-xs text-red-600 truncate max-w-[200px]" title={template.rejection_reason}>
                            {template.rejection_reason}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-600">{getCategoryLabel(template.category)}</td>
                    <td className="py-3 px-4 text-gray-600">{template.language}</td>
                        <td className="py-3 px-4 text-gray-600 text-sm">{formatDate(template.created_at)}</td>
                        <td className="py-3 px-4">
                          <div className="text-xs text-gray-500 font-mono" title={`ID: ${template.id}`}>
                            {template.id ? template.id.substring(0, 12) + '...' : '-'}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleViewDetails(template)}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditTemplate(template)}
                          className="text-blue-600 hover:text-blue-700 transition-colors"
                          title={template.status === 'APPROVED' ? 'Editar template (criará nova versão)' : 'Editar template'}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedTemplate && (
        <TemplateDetailsModal
          template={selectedTemplate}
          onClose={() => setSelectedTemplate(null)}
        />
      )}

      {showCreateModal && (
        <CreateTemplateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateTemplate}
          creating={creating}
        />
      )}

      {editingTemplate && (
        <EditTemplateModal
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onUpdate={handleUpdateTemplate}
          updating={updating}
        />
      )}

      <NotificationComponent />
    </div>
  );
};

interface TemplateDetailsModalProps {
  template: WhatsAppTemplateDetailed;
  onClose: () => void;
}

const TemplateDetailsModal: React.FC<TemplateDetailsModalProps> = ({ template, onClose }) => {
  const getStatusBadge = (status: string, showIcon: boolean = true) => {
    switch (status) {
      case 'APPROVED':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 flex items-center gap-1">
            {showIcon && <CheckCircle className="h-3 w-3" />}
            Aprovado
          </span>
        );
      case 'PENDING':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 flex items-center gap-1">
            {showIcon && <Clock className="h-3 w-3" />}
            Pendente
          </span>
        );
      case 'REJECTED':
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 flex items-center gap-1">
            {showIcon && <XCircle className="h-3 w-3" />}
            Rejeitado
          </span>
        );
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Detalhes do Template</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <p className="text-gray-900">{template.name}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status Atual</label>
              <div className="flex items-center gap-2">
                {getStatusBadge(template.status)}
                {template.status === 'APPROVED' && (
                  <span title="Template aprovado e pronto para uso">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </span>
                )}
                {template.status === 'PENDING' && (
                  <span title="Aguardando aprovação do Meta">
                    <Clock className="h-4 w-4 text-yellow-600" />
                  </span>
                )}
                {template.status === 'REJECTED' && (
                  <span title="Template rejeitado">
                    <XCircle className="h-4 w-4 text-red-600" />
                  </span>
                )}
              </div>
              {template.status === 'PENDING' && (
                <p className="text-xs text-yellow-600 mt-1">
                  Este template está aguardando revisão e aprovação do Meta. O processo pode levar até 24 horas.
                </p>
              )}
              {template.status === 'REJECTED' && template.rejection_reason && (
                <p className="text-xs text-red-600 mt-1">
                  Motivo: {template.rejection_reason}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
              <p className="text-gray-900">{template.category}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Idioma</label>
              <p className="text-gray-900">{template.language}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quality Rating</label>
              <p className="text-gray-900">{template.quality_rating || 'Não disponível'}</p>
            </div>
          </div>
          {template.created_at && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Criado em</label>
              <p className="text-gray-900">{formatDate(template.created_at)}</p>
            </div>
          )}
          {template.updated_at && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Atualizado em</label>
              <p className="text-gray-900">{formatDate(template.updated_at)}</p>
            </div>
          )}
          {template.rejection_reason && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da Rejeição</label>
              <p className="text-red-600">{template.rejection_reason}</p>
            </div>
          )}
          {template.components && template.components.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Componentes</label>
              <div className="space-y-3">
                {template.components.map((component, index) => (
                  <div key={index} className="border rounded p-3">
                    <div className="font-medium text-gray-900 mb-1">{component.type}</div>
                    {component.text && (
                      <p className="text-gray-700 text-sm whitespace-pre-wrap">{component.text}</p>
                    )}
                    {component.format && (
                      <p className="text-gray-500 text-xs mt-1">Formato: {component.format}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="p-6 border-t flex justify-between items-center">
          <div className="text-xs text-gray-500">
            {template.status === 'APPROVED' && 'Template aprovado e disponível para uso'}
            {template.status === 'PENDING' && 'Template aguardando aprovação do Meta'}
            {template.status === 'REJECTED' && 'Template rejeitado - verifique o motivo acima'}
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

interface EditTemplateModalProps {
  template: WhatsAppTemplateDetailed;
  onClose: () => void;
  onUpdate: (request: CreateTemplateRequest) => Promise<void>;
  updating: boolean;
}

const EditTemplateModal: React.FC<EditTemplateModalProps> = ({ template, onClose, onUpdate, updating }) => {
  const [formData, setFormData] = useState<CreateTemplateRequest>(() => ({
    name: template.name,
    category: template.category,
    language: template.language,
    components: (template.components || []).map((comp: any) => ({
      type: (comp.type || '').toUpperCase() as 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS', // Normalizar para maiúsculas
      format: comp.format as 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | undefined,
      text: comp.text,
      example: comp.example,
      buttons: comp.buttons
    })) as TemplateComponent[]
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return;
    }
    
    // Validar limites de caracteres
    const headerComponent = formData.components.find(c => c.type === 'HEADER');
    if (headerComponent && headerComponent.text && headerComponent.text.length > 60) {
      return;
    }
    
    const bodyComponent = formData.components.find(c => c.type === 'BODY');
    if (bodyComponent && bodyComponent.text && bodyComponent.text.length > 1024) {
      return;
    }
    
    const footerComponent = formData.components.find(c => c.type === 'FOOTER');
    if (footerComponent && footerComponent.text && footerComponent.text.length > 60) {
      return;
    }
    
    await onUpdate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Editar Template</h2>
            <p className="text-sm text-gray-500 mt-1">Status atual: {template.status}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={updating}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {template.status === 'APPROVED' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Informação:</strong> Ao editar este template aprovado, será criada uma nova versão que precisará ser aprovada novamente pelo Meta. 
                O template atual continuará ativo até que a nova versão seja aprovada.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Nome do Template *
                </label>
                <span className={`text-xs font-medium ${
                  formData.name.length > 512
                    ? 'text-red-600'
                    : formData.name.length > 450
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {formData.name.length}/512
                </span>
              </div>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 512 caracteres
                  if (value.length <= 512) {
                    setFormData({ ...formData, name: value });
                  }
                }}
                placeholder="ex: clientes_inativos"
                maxLength={512}
                className={`input w-full ${
                  formData.name.length > 512
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                required
                disabled={updating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo de 512 caracteres (limite oficial Meta API). Use apenas letras minúsculas, números e underscores.
              </p>
            </div>
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                className="input w-full"
                required
                disabled={updating}
              >
                <option value="UTILITY">Utilidade</option>
                <option value="MARKETING">Marketing</option>
                <option value="AUTHENTICATION">Autenticação</option>
              </select>
            </div>
            <div className="md:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Idioma *
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="input w-full"
                required
                disabled={updating}
              >
                <option value="pt_BR">Português (Brasil)</option>
                <option value="en_US">English (US)</option>
                <option value="es_ES">Español</option>
              </select>
            </div>
          </div>

          <div className="border-t pt-6 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Cabeçalho (Header) <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <span className={`text-xs font-medium ${
                  (formData.components.find(c => c.type === 'HEADER')?.text || '').length > 60
                    ? 'text-red-600'
                    : (formData.components.find(c => c.type === 'HEADER')?.text || '').length > 50
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {(formData.components.find(c => c.type === 'HEADER')?.text || '').length}/60
                </span>
              </div>
              <textarea
                value={formData.components.find(c => c.type === 'HEADER')?.text || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 60 caracteres
                  if (value.length > 60) return;
                  
                  const newComponents = [...formData.components];
                  const headerIndex = newComponents.findIndex(c => c.type === 'HEADER');
                  
                  if (value.trim()) {
                    if (headerIndex >= 0) {
                      newComponents[headerIndex] = {
                        ...newComponents[headerIndex],
                        type: 'HEADER',
                        text: value
                      };
                    } else {
                      newComponents.push({
                        type: 'HEADER',
                        text: value
                      });
                    }
                  } else {
                    // Remover se estiver vazio
                    if (headerIndex >= 0) {
                      newComponents.splice(headerIndex, 1);
                    }
                  }
                  
                  setFormData({ ...formData, components: newComponents });
                }}
                rows={3}
                maxLength={60}
                className={`input w-full font-mono text-sm resize-y ${
                  (formData.components.find(c => c.type === 'HEADER')?.text || '').length > 60
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                disabled={updating}
                placeholder="Digite o cabeçalho do template. Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis."
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo de 60 caracteres (limite oficial Meta API). Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Mensagem do Template (Body) *
                </label>
                <span className={`text-xs font-medium ${
                  (formData.components.find(c => c.type === 'BODY')?.text || '').length > 1024
                    ? 'text-red-600'
                    : (formData.components.find(c => c.type === 'BODY')?.text || '').length > 900
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {(formData.components.find(c => c.type === 'BODY')?.text || '').length}/1024
                </span>
              </div>
              <textarea
                value={formData.components.find(c => c.type === 'BODY')?.text || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 1024 caracteres
                  if (value.length > 1024) return;
                  
                  const newComponents = [...formData.components];
                  const bodyIndex = newComponents.findIndex(c => c.type === 'BODY');
                  
                  if (bodyIndex >= 0) {
                    newComponents[bodyIndex] = {
                      ...newComponents[bodyIndex],
                      type: 'BODY',
                      text: value
                    };
                  } else {
                    newComponents.push({
                      type: 'BODY',
                      text: value
                    });
                  }
                  
                  setFormData({ ...formData, components: newComponents });
                }}
                rows={12}
                maxLength={1024}
                className={`input w-full font-mono text-sm resize-y min-h-[200px] ${
                  (formData.components.find(c => c.type === 'BODY')?.text || '').length > 1024
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                required
                disabled={updating}
                placeholder="Digite a mensagem do template. Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis."
              />
              <div className="mt-2 flex flex-col gap-1">
                <p className="text-xs text-gray-500">
                  Máximo de 1024 caracteres (limite oficial Meta API). Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis.
                </p>
                {template.status === 'APPROVED' && (
                  <p className="text-xs text-blue-600">
                    Uma nova versão deste template será criada e enviada para aprovação.
                  </p>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Rodapé (Footer) <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <span className={`text-xs font-medium ${
                  (formData.components.find(c => c.type === 'FOOTER')?.text || '').length > 60
                    ? 'text-red-600'
                    : (formData.components.find(c => c.type === 'FOOTER')?.text || '').length > 50
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {(formData.components.find(c => c.type === 'FOOTER')?.text || '').length}/60
                </span>
              </div>
              <textarea
                value={formData.components.find(c => c.type === 'FOOTER')?.text || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 60 caracteres
                  if (value.length > 60) return;
                  
                  const newComponents = [...formData.components];
                  const footerIndex = newComponents.findIndex(c => c.type === 'FOOTER');
                  
                  if (value.trim()) {
                    if (footerIndex >= 0) {
                      newComponents[footerIndex] = {
                        ...newComponents[footerIndex],
                        type: 'FOOTER',
                        text: value
                      };
                    } else {
                      newComponents.push({
                        type: 'FOOTER',
                        text: value
                      });
                    }
                  } else {
                    // Remover se estiver vazio
                    if (footerIndex >= 0) {
                      newComponents.splice(footerIndex, 1);
                    }
                  }
                  
                  setFormData({ ...formData, components: newComponents });
                }}
                rows={2}
                maxLength={60}
                className={`input w-full font-mono text-sm resize-y ${
                  (formData.components.find(c => c.type === 'FOOTER')?.text || '').length > 60
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                disabled={updating}
                placeholder="Digite o rodapé do template."
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo de 60 caracteres (limite oficial Meta API). Não pode conter variáveis.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              disabled={updating}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
              disabled={updating || !formData.name.trim()}
            >
              {updating ? (
                <>
                  <Loader className="h-4 w-4 animate-spin inline mr-2" />
                  Atualizando...
                </>
              ) : (
                'Atualizar Template'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface CreateTemplateModalProps {
  onClose: () => void;
  onCreate: (request: CreateTemplateRequest) => Promise<void>;
  creating: boolean;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({ onClose, onCreate, creating }) => {
  const { showError } = useNotification();
  const [formData, setFormData] = useState<CreateTemplateRequest>({
    name: '',
    category: 'UTILITY',
    language: 'pt_BR',
    components: [
      {
        type: 'BODY',
        text: 'Olá! Esta é uma mensagem de teste.'
      }
    ]
  });

  useEffect(() => {
    console.log('CreateTemplateModal montado');
    return () => {
      console.log('CreateTemplateModal desmontado');
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = formData.name.trim();
    
    if (!trimmedName) {
      showError('Erro', 'O nome do template é obrigatório.');
      return;
    }
    
    // Validar formato do nome conforme documentação Meta (máximo 512 caracteres, apenas minúsculas, números e underscores)
    const nameRegex = /^[a-z0-9_]+$/;
    if (!nameRegex.test(trimmedName)) {
      showError('Erro', 'O nome do template deve conter apenas letras minúsculas, números e underscores. Não use espaços, maiúsculas ou caracteres especiais.');
      return;
    }
    
    if (trimmedName.length > 512) {
      showError('Erro', 'O nome do template não pode ter mais de 512 caracteres.');
      return;
    }
    
    // Validar se há pelo menos um componente BODY
    if (!formData.components || formData.components.length === 0) {
      showError('Erro', 'O template deve ter pelo menos uma mensagem (componente BODY).');
      return;
    }
    
    // Validar se o componente BODY tem texto
    const bodyComponent = formData.components.find(c => c.type === 'BODY');
    if (!bodyComponent || !bodyComponent.text || !bodyComponent.text.trim()) {
      showError('Erro', 'A mensagem do template não pode estar vazia.');
      return;
    }
    
    // Validar limites de caracteres
    const headerComponent = formData.components.find(c => c.type === 'HEADER');
    if (headerComponent && headerComponent.text && headerComponent.text.length > 60) {
      showError('Erro', 'O cabeçalho (Header) não pode ter mais de 60 caracteres.');
      return;
    }
    
    if (bodyComponent.text && bodyComponent.text.length > 1024) {
      showError('Erro', 'A mensagem (Body) não pode ter mais de 1024 caracteres.');
      return;
    }
    
    const footerComponent = formData.components.find(c => c.type === 'FOOTER');
    if (footerComponent && footerComponent.text && footerComponent.text.length > 60) {
      showError('Erro', 'O rodapé (Footer) não pode ter mais de 60 caracteres.');
      return;
    }
    
    // Garantir que o nome está em minúsculas
    const requestWithLowercaseName = {
      ...formData,
      name: trimmedName.toLowerCase()
    };
    
    await onCreate(requestWithLowercaseName);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !creating) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Criar Novo Template</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={creating}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Nome do Template *
              </label>
              <span className={`text-xs font-medium ${
                formData.name.length > 512
                  ? 'text-red-600'
                  : formData.name.length > 450
                  ? 'text-yellow-600'
                  : 'text-gray-500'
              }`}>
                {formData.name.length}/512
              </span>
            </div>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                const value = e.target.value;
                // Limitar a 512 caracteres
                if (value.length <= 512) {
                  setFormData({ ...formData, name: value });
                }
              }}
              placeholder="ex: clientes_inativos"
              maxLength={512}
              className={`input w-full ${
                formData.name.length > 512
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : ''
              }`}
              required
              disabled={creating}
            />
            <p className="text-xs text-gray-500 mt-1">
              Máximo de 512 caracteres (limite oficial Meta API). Use apenas letras minúsculas, números e underscores. Não use espaços ou caracteres especiais.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                className="input w-full"
                required
                disabled={creating}
              >
                <option value="UTILITY">Utilidade</option>
                <option value="MARKETING">Marketing</option>
                <option value="AUTHENTICATION">Autenticação</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Idioma *
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="input w-full"
                required
                disabled={creating}
              >
                <option value="pt_BR">Português (Brasil)</option>
                <option value="en_US">English (US)</option>
                <option value="es_ES">Español</option>
              </select>
            </div>
          </div>

          <div className="border-t pt-6 space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Cabeçalho (Header) <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <span className={`text-xs font-medium ${
                  (formData.components.find(c => c.type === 'HEADER')?.text || '').length > 60
                    ? 'text-red-600'
                    : (formData.components.find(c => c.type === 'HEADER')?.text || '').length > 50
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {(formData.components.find(c => c.type === 'HEADER')?.text || '').length}/60
                </span>
              </div>
              <textarea
                value={formData.components.find(c => c.type === 'HEADER')?.text || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 60 caracteres
                  if (value.length > 60) return;
                  
                  const newComponents = [...formData.components];
                  const headerIndex = newComponents.findIndex(c => c.type === 'HEADER');
                  
                  if (value.trim()) {
                    if (headerIndex >= 0) {
                      newComponents[headerIndex] = {
                        ...newComponents[headerIndex],
                        type: 'HEADER',
                        text: value
                      };
                    } else {
                      newComponents.push({
                        type: 'HEADER',
                        text: value
                      });
                    }
                  } else {
                    if (headerIndex >= 0) {
                      newComponents.splice(headerIndex, 1);
                    }
                  }
                  
                  setFormData({ ...formData, components: newComponents });
                }}
                rows={3}
                maxLength={60}
                className={`input w-full font-mono text-sm resize-y ${
                  (formData.components.find(c => c.type === 'HEADER')?.text || '').length > 60
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                disabled={creating}
                placeholder="Digite o cabeçalho do template. Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis."
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo de 60 caracteres (limite oficial Meta API). Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Mensagem do Template (Body) *
                </label>
                <span className={`text-xs font-medium ${
                  (formData.components.find(c => c.type === 'BODY')?.text || '').length > 1024
                    ? 'text-red-600'
                    : (formData.components.find(c => c.type === 'BODY')?.text || '').length > 900
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {(formData.components.find(c => c.type === 'BODY')?.text || '').length}/1024
                </span>
              </div>
              <textarea
                value={formData.components.find(c => c.type === 'BODY')?.text || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 1024 caracteres
                  if (value.length > 1024) return;
                  
                  const newComponents = [...formData.components];
                  const bodyIndex = newComponents.findIndex(c => c.type === 'BODY');
                  
                  if (bodyIndex >= 0) {
                    newComponents[bodyIndex] = {
                      ...newComponents[bodyIndex],
                      type: 'BODY',
                      text: value
                    };
                  } else {
                    newComponents.push({
                      type: 'BODY',
                      text: value
                    });
                  }
                  
                  setFormData({ ...formData, components: newComponents });
                }}
                rows={12}
                maxLength={1024}
                className={`input w-full font-mono text-sm resize-y min-h-[200px] ${
                  (formData.components.find(c => c.type === 'BODY')?.text || '').length > 1024
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                required
                disabled={creating}
                placeholder="Digite a mensagem do template. Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis."
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo de 1024 caracteres (limite oficial Meta API). Use {'{{1}}'}, {'{{2}}'}, etc. para variáveis.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Rodapé (Footer) <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <span className={`text-xs font-medium ${
                  (formData.components.find(c => c.type === 'FOOTER')?.text || '').length > 60
                    ? 'text-red-600'
                    : (formData.components.find(c => c.type === 'FOOTER')?.text || '').length > 50
                    ? 'text-yellow-600'
                    : 'text-gray-500'
                }`}>
                  {(formData.components.find(c => c.type === 'FOOTER')?.text || '').length}/60
                </span>
              </div>
              <textarea
                value={formData.components.find(c => c.type === 'FOOTER')?.text || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  // Limitar a 60 caracteres
                  if (value.length > 60) return;
                  
                  const newComponents = [...formData.components];
                  const footerIndex = newComponents.findIndex(c => c.type === 'FOOTER');
                  
                  if (value.trim()) {
                    if (footerIndex >= 0) {
                      newComponents[footerIndex] = {
                        ...newComponents[footerIndex],
                        type: 'FOOTER',
                        text: value
                      };
                    } else {
                      newComponents.push({
                        type: 'FOOTER',
                        text: value
                      });
                    }
                  } else {
                    if (footerIndex >= 0) {
                      newComponents.splice(footerIndex, 1);
                    }
                  }
                  
                  setFormData({ ...formData, components: newComponents });
                }}
                rows={2}
                maxLength={60}
                className={`input w-full font-mono text-sm resize-y ${
                  (formData.components.find(c => c.type === 'FOOTER')?.text || '').length > 60
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : ''
                }`}
                disabled={creating}
                placeholder="Digite o rodapé do template."
              />
              <p className="text-xs text-gray-500 mt-1">
                Máximo de 60 caracteres (limite oficial Meta API). Não pode conter variáveis.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              disabled={creating}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
              disabled={creating || !formData.name.trim()}
            >
              {creating ? (
                <>
                  <Loader className="h-4 w-4 animate-spin inline mr-2" />
                  Criando...
                </>
              ) : (
                'Criar Template'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Templates;

